import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFunds } from "../lib/useFunds.js";
import { normalize, readWorkspace, writeWorkspace, datDauXoa } from "../lib/workspace.js";
import { themMuc, boMuc, coTrongDanhSach, GOI_Y_TOI_DA } from "@shared/danhSachMucTieu.js";
import { label as taxonomyLabel } from "@shared/taxonomy.js";
import {
  fundTypeKey,
  fundTypeLabel,
  typeLabel,
  ticketText as ticket,
  ticketLabel as nhanTicket,
  ticketBadge as badgeTicket,
  CAPITAL_FILTERS,
  matchesCapital,
  fundCta,
} from "../lib/fundLabels.js";
import { track } from "../lib/track.js";
import NguonVonBanDo from "../components/NguonVonBanDo.jsx";
import LogoQuy from "../components/LogoQuy.jsx";
import { useChuongTrinh } from "../lib/programs.js";
import { phanNhom, TRANG_THAI } from "@shared/programs.js";
import { homNay as layHomNay } from "../lib/format.js";
import { docChon, ghiChon, locVaDem } from "@shared/locMuc.js";
import { KhungHaiCot, NhomLoc, OTim, DongDem } from "../components/LocHaiCot.jsx";

/** Màu từng loại đơn vị: dùng cho chấm ở chip lọc và nhãn trên thẻ. */
const MAU_LOAI = {
  VC: "#86cfaa", CVC: "#9fb7e0", ACCELERATOR: "#f4a623", ANGEL: "#e9a48e", ANGEL_NETWORK: "#e9a48e",
  LENDER: "#c3b1e6", GRANT: "#7fc4c9", GOV: "#7fc4c9", IMPACT_VC: "#b5d17a", PE_GROWTH: "#d7a8c8",
};
const mauLoai = (k) => MAU_LOAI[k] || "#a9bccd";

const THU_TU_GD = ["IDEA", "PROTOTYPE", "ANGEL", "PRESEED", "SEED", "SEED_PLUS", "SERIES_A", "LATER"];
// Khách đến từ trợ lý AI thường cần một bảng so sánh nhanh hơn là duyệt thẻ.
const TU_TRO_LY_AI = /utm_source=(chatgpt|openai|perplexity|gemini|copilot|claude)/i;
const TRO_LY_AI_REF = /(chatgpt|openai|perplexity|gemini\.google|copilot|claude\.ai)/i;

// Nhãn thẻ: chỉ những gì đơn vị đã công bố; không có thì không hiện gì.
function nhanThe(domain, values) {
  return (values || []).map((v) => {
    try { return taxonomyLabel(domain, v) || v; } catch { return v; }
  });
}
function ngayNgan(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s)) ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : "";
}
function names(domain, values) {
  return (
    (values || [])
      .map((v) => {
        try {
          return taxonomyLabel(domain, v) || v;
        } catch {
          return v;
        }
      })
      .join(", ") ||
    // Giai đoạn trống thường là "quỹ đầu tư sớm nhưng không nêu vòng nào",
    // nên nói đúng như vậy thay vì một chữ "Chưa công bố" chung chung.
    (domain === "STAGE" ? "Chưa công bố vòng cụ thể" : "Chưa công bố")
  );
}
export default function Funding() {
  const { funds, nguon, loi } = useFunds();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const chon = {
    type: docChon(params, "type"), stage: docChon(params, "stage"), nganh: docChon(params, "nganh"),
    von: docChon(params, "von"), geo: docChon(params, "geo"),
  };
  // Giá trị đơn cho các khối chỉ hiểu một lựa chọn (khung biểu đồ đầu trang, câu "ghi rõ ngành").
  const mot = (k) => (chon[k].length === 1 ? chon[k][0] : "");
  const stage = mot("stage"), type = mot("type"), nganh = mot("nganh");
  // Kiểu xem: thẻ (mặc định) hoặc bảng. Khách từ trợ lý AI trên máy tính mở thẳng dạng bảng.
  const [xemMacDinh, setXemMacDinh] = useState("the");
  const xem = params.get("xem") || xemMacDinh;
  const [saved, setSaved] = useState([]),
    [compare, setCompare] = useState([]),
    [message, setMessage] = useState("");
  const khoiSoSanh = useRef(null);
  const ketQua = useRef(null);
  const chuongTrinh = useChuongTrinh();
  // null lúc prerender: "còn N ngày" tính lúc build sẽ sai ngay hôm sau.
  const [homNay, setHomNay] = useState(null);
  useEffect(() => {
    setSaved(readWorkspace().saved || []);
    setHomNay(layHomNay());
    try {
      const tuAI = TU_TRO_LY_AI.test(window.location.search) || TRO_LY_AI_REF.test(document.referrer || "");
      if (tuAI && window.matchMedia("(min-width: 900px)").matches) setXemMacDinh("bang");
    } catch { /* bỏ qua */ }
  }, []);
  const change = (k, v) => {
    // Không gửi nội dung ô tìm kiếm — người dùng có thể gõ tên công ty mình.
    if (k !== "q" && v) track("funding_filter_use", { filter: k, value: v });
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p, { replace: true });
  };
  const all = funds.filter(
    (f) => (f.publish?.status ?? "PUBLISHED") === "PUBLISHED" && !f.isExample,
  );
  // Ô tìm khớp cả tên ngành (Việt và Anh) để gõ "fintech", "y tế" cũng ra.
  const chuTim = (f) => normalize([
    f.name, f.content?.summary || "",
    ...(f.thesis?.industries || []).flatMap((i) => { try { return [taxonomyLabel("INDUSTRY", i), taxonomyLabel("INDUSTRY", i, "en")]; } catch { return [i]; } }),
  ].join(" "));
  // Lọc ngành: quỹ ghi rõ ngành lên trước; quỹ chưa công bố ngành vẫn giữ lại
  // ở nhóm sau, vì nhiều quỹ đầu tư đa ngành mà không liệt kê.
  const NHOM_LOC = [
    { key: "type", giaTri: (f) => [fundTypeKey(f)].filter(Boolean) },
    { key: "stage", giaTri: (f) => f.thesis?.stages || [] },
    { key: "nganh", giaTri: (f) => f.thesis?.industries || [],
      khop: (f, ds) => !(f.thesis?.industries || []).length || ds.some((v) => f.thesis.industries.includes(v)) },
    { key: "von", giaTri: (f) => CAPITAL_FILTERS.filter((o) => o.value && matchesCapital(f, o.value)).map((o) => o.value) },
    { key: "geo", giaTri: (f) => f.thesis?.geos || [] },
  ];
  const { ketQua: locChung, dem } = locVaDem(all, NHOM_LOC, chon, (f) => chuTim(f).includes(normalize(q)));
  const coNganhChon = (f) => chon.nganh.some((v) => (f.thesis?.industries || []).includes(v));
  const ghiRoNganh = chon.nganh.length ? locChung.filter(coNganhChon) : locChung;
  const chuaCongBoNganh = chon.nganh.length ? locChung.filter((f) => !coNganhChon(f)) : [];
  const tenNganhChon = chon.nganh.map((x) => names("INDUSTRY", [x])).join(", ");
  const list = [...ghiRoNganh, ...chuaCongBoNganh];
  function toggle(f) {
    try {
      const d = readWorkspace();
      const co = coTrongDanhSach(d.saved, f.fundId);
      const moi = co
        ? { ...datDauXoa(d, `saved:${f.fundId}`), saved: boMuc(d.saved, f.fundId) }
        : { ...d, saved: themMuc(d.saved, { id: f.fundId, name: f.name }) };
      writeWorkspace(moi);
      setSaved(moi.saved);
      if (!co) {
        track("funding_source_save", { fund_id: f.fundId });
        setMessage(moi.saved.length > GOI_Y_TOI_DA
          ? `Đã thêm ${f.name}. Danh sách mục tiêu đang có ${moi.saved.length} nơi; nên giữ khoảng 15 đến 20.`
          : `Đã thêm ${f.name} vào danh sách mục tiêu (${moi.saved.length}/${GOI_Y_TOI_DA}).`);
      } else {
        setMessage(`Đã bỏ ${f.name} khỏi danh sách mục tiêu.`);
      }
    } catch (e) {
      setMessage(e?.message?.includes("Bỏ bớt") ? e.message : "Chưa lưu được. Kiểm tra bộ nhớ trình duyệt.");
    }
  }
  const doiSoSanh = (id) =>
    setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const selected = all.filter((f) => compare.includes(f.fundId));

  // Bộ lọc đang dùng (chỉ để biết có đang lọc hay không).
  const dangLoc = [...Object.values(chon).flat(), q].filter(Boolean);
  const cacLoai = Object.entries(all.reduce((a, f) => { const k = fundTypeKey(f); if (k) a[k] = (a[k] || 0) + 1; return a; }, {}))
    .sort((a, b) => b[1] - a[1]);
  const cacGiaiDoan = [...new Set(all.flatMap((f) => f.thesis?.stages || []))]
    .sort((a, b) => (THU_TU_GD.indexOf(a) + 99) % 99 - (THU_TU_GD.indexOf(b) + 99) % 99);
  const demNganh = all.reduce((a, f) => { (f.thesis?.industries || []).forEach((x) => { a[x] = (a[x] || 0) + 1; }); return a; }, {});
  const cacNganh = Object.entries(demNganh).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  const doiNhom = (k) => (ds) => {
    if (ds.length > chon[k].length) track("funding_filter_use", { filter: k, value: ds[ds.length - 1] });
    setParams(ghiChon(params, k, ds), { replace: true });
  };
  // Hạn gần nhất lên đầu (phanNhom đã sắp), tối đa 8 thẻ.
  const dangMo = homNay ? phanNhom(chuongTrinh, homNay).dangMo.slice(0, 8) : [];

  const veThe = (f) => {
    const daLuu = saved.some((x) => x.id === f.fundId);
    const dangSoSanh = compare.includes(f.fundId);
    const giaiDoan = nhanThe("STAGE", f.thesis?.stages);
    const diaBan = nhanThe("GEO", f.thesis?.geos);
    const coTien = !!(f.thesis?.ticket?.min || f.thesis?.ticket?.max);
    const dm = f.portfolio?.items || [];
    const k = fundTypeKey(f);
    return (
      <article className="nvt" id={"fund-" + f.fundId} key={f.fundId} style={{ "--mau": mauLoai(k) }}>
        <div className="nvt-dau">
          <LogoQuy f={f} className="nvt-logo" />
          <span className="nvt-loai"><i aria-hidden="true" />{fundTypeLabel(f)}</span>
        </div>
        <h2 className="nvt-ten"><Link to={`/nguon-von/${f.slug}`}>{f.name}</Link></h2>
        <p className="nvt-meta">
          {giaiDoan.length ? giaiDoan.join(", ") : "Chưa công bố vòng cụ thể"}
          {diaBan.length > 0 && <span className="nvt-dia"><svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>{diaBan.join(", ")}</span>}
        </p>
        {coTien && (
          <p className="nvt-tien">
            <span>{nhanTicket(f)}</span> <strong>{ticket(f)}</strong>
            {badgeTicket(f) && <span className="nvt-canh-bao">{badgeTicket(f)}</span>}
          </p>
        )}
        {f.content?.summary && <p className="nvt-tom-tat">{f.content.summary}</p>}
        {dm.length > 0 && (
          <p className="nvt-dm">
            Đã đầu tư {dm.slice(0, 3).map((x) => x.name).join(", ")}{dm.length > 3 ? ` và ${dm.length - 3} nơi khác` : ""}
          </p>
        )}
        <div className="nvt-chan">
          <span className="nvt-kiem-chung" title={f.provenance?.lastVerifiedAt ? `Đối chiếu với nguồn chính thức ngày ${f.provenance.lastVerifiedAt}` : "Chưa xác minh"}>{f.provenance?.lastVerifiedAt ? `Kiểm chứng ${Number(f.provenance.lastVerifiedAt.slice(5, 7))}/${f.provenance.lastVerifiedAt.slice(0, 4)}` : "Chưa xác minh"}</span>
          <button type="button" className={"nvq-nut" + (daLuu ? " bat" : "")} aria-pressed={daLuu}
            aria-label={daLuu ? `Bỏ ${f.name} khỏi mục tiêu` : `Thêm ${f.name} vào mục tiêu`} title={daLuu ? "Đã trong mục tiêu" : "Thêm vào mục tiêu"} onClick={() => toggle(f)}>
            <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill={daLuu ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
          </button>
          <button type="button" className={"nvq-nut" + (dangSoSanh ? " bat" : "")} aria-pressed={dangSoSanh} aria-label={`So sánh ${f.name}`}
            title={!dangSoSanh && compare.length === 3 ? "Đã chọn đủ 3 nguồn vốn" : "So sánh"} disabled={!dangSoSanh && compare.length === 3} onClick={() => doiSoSanh(f.fundId)}>
            <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h7M3 12h7M3 18h7M14 6h7M14 12h7M14 18h7" /></svg>
          </button>
          <Link className="nvt-xem" to={`/nguon-von/${f.slug}`}>Xem điều kiện →</Link>
        </div>
      </article>
    );
  };

  // Một dòng của dạng bảng: đủ để so sánh và đi tiếp ngay, không cần mở từng quỹ.
  const veDong = (f) => {
    const daLuu = saved.some((x) => x.id === f.fundId);
    const giaiDoan = nhanThe("STAGE", f.thesis?.stages);
    // Ngành đang lọc đứng đầu ô ngành để thấy ngay vì sao quỹ có mặt.
    const nganhQuy = nhanThe("INDUSTRY", [...(f.thesis?.industries || [])].sort((x, y) => chon.nganh.includes(y) - chon.nganh.includes(x)));
    const coTien = !!(f.thesis?.ticket?.min || f.thesis?.ticket?.max);
    const cta = fundCta(f);
    const k = fundTypeKey(f);
    return (
      <tr key={f.fundId} style={{ "--mau": mauLoai(k) }}>
        <th scope="row">
          <div className="nvg-ten">
            <LogoQuy f={f} className="nvg-logo" />
            <div>
              <Link to={`/nguon-von/${f.slug}`}>{f.name}</Link>
              <span className="nvt-loai"><i aria-hidden="true" />{fundTypeLabel(f)}</span>
            </div>
          </div>
        </th>
        <td>{giaiDoan.length ? giaiDoan.join(", ") : <span className="nvg-mo">Chưa công bố</span>}</td>
        <td>{coTien ? <>{ticket(f)}{badgeTicket(f) && <span className="nvg-mo nvg-nho">{badgeTicket(f)}</span>}</> : <span className="nvg-mo">Chưa công bố</span>}</td>
        <td>{nganhQuy.length ? <span className="nvg-nganh">{nganhQuy.slice(0, 3).join(", ")}{nganhQuy.length > 3 ? ` và ${nganhQuy.length - 3} ngành khác` : ""}</span> : <span className="nvg-mo">Chưa công bố</span>}</td>
        <td>
          {cta
            ? <a href={cta.href} onClick={() => track("funding_cta_click", { fund_id: f.fundId, kind: cta.kind })} {...(cta.external ? { target: "_blank", rel: "noreferrer" } : {})}>{cta.label}</a>
            : <Link to={`/nguon-von/${f.slug}`}>Xem trang quỹ →</Link>}
        </td>
        <td>
          <button type="button" className={"nvq-nut" + (daLuu ? " bat" : "")} aria-pressed={daLuu}
            aria-label={daLuu ? `Bỏ ${f.name} khỏi mục tiêu` : `Thêm ${f.name} vào mục tiêu`} title={daLuu ? "Đã trong mục tiêu" : "Thêm vào mục tiêu"} onClick={() => toggle(f)}>
            <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill={daLuu ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
          </button>
        </td>
      </tr>
    );
  };

  return (
    <>
      {/* Đầu trang: lời giới thiệu + khung trực quan từ số liệu thật của danh mục.
          Chạm cột giai đoạn / thẻ loại đơn vị là lọc danh sách bên dưới. */}
      <section className="nguon-von-dau nv-dau">
        <div className="nv-dau-chu">
          <p className="eyebrow">NGUỒN VỐN</p>
          <h1>Quỹ đầu tư và nguồn vốn cho startup Việt Nam</h1>
          <p className="lead">
            {all.length} quỹ, mạng lưới angel và chương trình, mỗi nơi ghi nguồn chính thức và ngày kiểm chứng.
          </p>
          <p className="nv-dau-lien-ket">
            <Link to="/cong-cu/quy-phu-hop">Đối chiếu với startup của tôi →</Link>
            <Link to="/chuong-trinh">Chương trình hỗ trợ →</Link>
          </p>
        </div>
        <NguonVonBanDo
          all={all}
          stage={stage}
          type={type}
          onChon={(k, v) => {
            change(k, v);
            if (v) requestAnimationFrame(() => ketQua.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
          }}
        />
      </section>

      <div ref={ketQua} className="nv-ket-qua">
      <KhungHaiCot
        soDangChon={Object.values(chon).flat().length}
        boLoc={<>
          <NhomLoc tieuDe="Loại đơn vị" chon={chon.type} onDoi={doiNhom("type")}
            ds={cacLoai.map(([k]) => ({ v: k, nhan: typeLabel(k), dem: dem.type[k] || 0, mau: mauLoai(k) }))} />
          <NhomLoc tieuDe="Giai đoạn" chon={chon.stage} onDoi={doiNhom("stage")} gioiHan={8}
            ds={cacGiaiDoan.map((x) => ({ v: x, nhan: names("STAGE", [x]), dem: dem.stage[x] || 0 }))} />
          <NhomLoc tieuDe="Ngành" chon={chon.nganh} onDoi={doiNhom("nganh")} gioiHan={6}
            ds={cacNganh.map((x) => ({ v: x, nhan: names("INDUSTRY", [x]), dem: dem.nganh[x] || 0 }))} />
          <NhomLoc tieuDe="Hình thức vốn" chon={chon.von} onDoi={doiNhom("von")} moSan={chon.von.length > 0}
            ds={CAPITAL_FILTERS.filter((o) => o.value).map((o) => ({ v: o.value, nhan: o.label, dem: dem.von[o.value] || 0 }))} />
          <NhomLoc tieuDe="Địa bàn" chon={chon.geo} onDoi={doiNhom("geo")} moSan={chon.geo.length > 0}
            ds={[...new Set(all.flatMap((f) => f.thesis?.geos || []))].map((g) => ({ v: g, nhan: taxonomyLabel("GEO", g), dem: dem.geo[g] || 0 }))} />
        </>}
      >
        <OTim id="fund-search" nhan="Tìm theo tên hoặc lĩnh vực" value={q} onChange={(v) => change("q", v)} placeholder="Tìm: fintech, giáo dục, Do Ventures…" />
        <DongDem
          dangLoc={dangLoc.length > 0}
          onXoa={() => setParams(xem !== "the" && params.get("xem") ? { xem } : {}, { replace: true })}
          phai={
            <div className="nv-kieu-xem" role="group" aria-label="Kiểu xem">
              <button type="button" className={xem !== "bang" ? "dang" : ""} aria-pressed={xem !== "bang"} title="Xem dạng thẻ" onClick={() => change("xem", "the")}>
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
                Thẻ
              </button>
              <button type="button" className={xem === "bang" ? "dang" : ""} aria-pressed={xem === "bang"} title="Xem dạng bảng so sánh" onClick={() => change("xem", "bang")}>
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                Bảng
              </button>
            </div>
          }
        >
          {chon.nganh.length
            ? <><b>{ghiRoNganh.length} nguồn vốn ghi rõ {tenNganhChon}</b>{chuaCongBoNganh.length > 0 && `, thêm ${chuaCongBoNganh.length} nơi chưa công bố ngành`}</>
            : <>Hiển thị <b>{list.length}</b> trên {all.length} nguồn vốn</>}
          {nguon === "DANG_TAI" ? ". Đang kiểm tra bản cập nhật…" : loi ? ". Đang dùng danh mục đóng gói do chưa tải được cập nhật." : ""}
        </DongDem>
      <p role="status" className="ghi-chu">{message}</p>

      {!!selected.length && (
        <section className="surface section-space" ref={khoiSoSanh} id="so-sanh">
          <div className="section-bar">
            <h2>So sánh {selected.length}/3 nguồn vốn</h2>
            <button className="nut phu" onClick={() => setCompare([])}>
              Bỏ so sánh
            </button>
          </div>
          <div className="three-grid">
            {selected.map((f) => (
              <article key={f.fundId}>
                <h3>{f.name}</h3>
                <p><strong>{nhanTicket(f)}:</strong> {ticket(f)}</p>
                <p><strong>Loại:</strong> {fundTypeLabel(f)}</p>
                <p><strong>Giai đoạn:</strong> {names("STAGE", f.thesis?.stages)}</p>
                <p><strong>Địa bàn:</strong> {names("GEO", f.thesis?.geos)}</p>
                <p><strong>Kiểm chứng:</strong> {f.provenance?.lastVerifiedAt || "Chưa xác minh"}</p>
                <Link to={`/nguon-von/${f.slug}`}>Xem điều kiện và cách tiếp cận →</Link>
              </article>
            ))}
          </div>
        </section>
      )}

      {list.length > 0 && (xem === "bang" ? (
        <div className="nvg-cuon" tabIndex={0} aria-label="Bảng nguồn vốn, kéo ngang để xem thêm cột">
          <table className="nvg">
            <thead>
              <tr>
                <th scope="col">Nguồn vốn</th>
                <th scope="col">Giai đoạn</th>
                <th scope="col">Khoản đầu tư</th>
                <th scope="col">Ngành đã công bố</th>
                <th scope="col">Cách tiếp cận</th>
                <th scope="col"><span className="sr-only">Lưu</span></th>
              </tr>
            </thead>
            <tbody>
              {ghiRoNganh.map(veDong)}
              {chuaCongBoNganh.length > 0 && (
                <tr className="nvg-nhom"><th colSpan={6} scope="rowgroup">Chưa công bố ngành, có thể vẫn đầu tư {tenNganhChon}. Xem trang quỹ để chắc chắn.</th></tr>
              )}
              {chuaCongBoNganh.map(veDong)}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="luoi-quy nv-luoi">{ghiRoNganh.map(veThe)}</div>
          {chuaCongBoNganh.length > 0 && (
            <>
              <h2 className="nv-nhom-tieu-de">Chưa công bố ngành, có thể vẫn đầu tư {tenNganhChon}</h2>
              <div className="luoi-quy nv-luoi">{chuaCongBoNganh.map(veThe)}</div>
            </>
          )}
        </>
      ))}

      {!list.length && (
        <div className="empty-state">
          <h2>Chưa có nguồn vốn khớp với bộ lọc này</h2>
          <p>
            Nhiều đơn vị không công bố giai đoạn hoặc địa bàn, nên lọc càng hẹp
            càng dễ bỏ sót. Thử bỏ bớt một bộ lọc.
          </p>
          <button className="nut phu" onClick={() => setParams({})}>
            Xoá bộ lọc
          </button>
          <p>
            <Link to="/chuong-trinh">Tìm hiểu chương trình hỗ trợ →</Link>
          </p>
        </div>
      )}
      </KhungHaiCot>
      </div>

      {/* Chương trình đang nhận hồ sơ: đặt dưới danh sách để quỹ lọt màn hình đầu.
          Chỉ hiện khi chưa lọc, và chỉ sau khi trang chạy trên trình duyệt
          (trạng thái phụ thuộc ngày hôm nay). */}
      {dangMo.length > 0 && dangLoc.length === 0 && (
        <section className="ct-dang-mo" aria-labelledby="ct-dang-mo-tieu-de">
          <div className="ct-dang-mo-dau">
            <h2 id="ct-dang-mo-tieu-de">Chương trình đang nhận hồ sơ</h2>
            <Link to="/chuong-trinh">Xem tất cả chương trình →</Link>
          </div>
          <div className="ct-dang-mo-hang">
            {dangMo.map((ct) => (
              <Link key={ct.slug} to="/chuong-trinh" className="ct-dang-mo-the">
                <span className={"ct-dang-mo-nhan" + (ct.soNgayConLai != null && ct.soNgayConLai <= 30 ? " gap" : "")}>
                  {ct.trangThai === TRANG_THAI.LIEN_TUC
                    ? "Nhận hồ sơ liên tục"
                    : ct.soNgayConLai != null
                      ? `Hạn ${ngayNgan(ct.edition?.closesAt)}, còn ${ct.soNgayConLai} ngày`
                      : ct.nhanTrangThai}
                </span>
                <strong>{ct.name}</strong>
                {ct.organizer && <span className="ct-dang-mo-chu">{ct.organizer}</span>}
              </Link>
            ))}
          </div>
        </section>
      )}

      <p className="nv-ghi-chu">
        Danh mục chưa đại diện toàn bộ thị trường. Giai đoạn và địa bàn chỉ lọc theo thông tin đơn vị đã công bố. Xác minh điều kiện với đơn vị trước khi gửi hồ sơ.
      </p>
      <p className="ghi-chu-logo">
        Logo và tên thương hiệu thuộc về từng đơn vị. fundraising.vn không liên kết với các
        đơn vị trong danh mục. Đơn vị muốn gỡ logo hoặc sửa thông tin, vui lòng liên hệ chúng tôi.
      </p>


      {selected.length > 0 && (
        <div className="thanh-so-sanh" role="region" aria-label="So sánh nguồn vốn">
          <span>Đã chọn {selected.length}/3 để so sánh</span>
          <button
            type="button"
            className="nut"
            onClick={() => khoiSoSanh.current?.scrollIntoView({ block: "start", behavior: "smooth" })}
          >
            Xem so sánh ↑
          </button>
          <button type="button" className="nut phu" onClick={() => setCompare([])}>Bỏ</button>
        </div>
      )}
    </>
  );
}
